import type { OrgRole, AccountStatus } from "@document-rag/shared";

/**
 * Utility functions for user role and status badges
 * Used across multiple components like UserTable, ProfilePage, etc.
 */

export const getRoleTooltip = (role: OrgRole | string): string => {
  switch (role) {
    case "OWNER":
      return "Organization owner with full control over the organization";
    case "ADMIN":
      return "Organization administrator with user management permissions";
    case "MEMBER":
      return "Standard organization member with access to resources";
    case "VIEWER":
      return "Read-only access to organization resources";
    default:
      return "Organization role information";
  }
};

export const getStatusTooltip = (status: AccountStatus | string): string => {
  switch (status) {
    case "ACTIVE":
      return "Account is active and fully functional";
    case "UNVERIFIED":
      return "Account email address has not been verified yet";
    case "CREATED":
      return "Account has been created but setup is not complete";
    case "ONBOARDING":
      return "Onboarding email has been sent, awaiting user activation";
    case "SUSPENDED":
      return "Account has been temporarily suspended by an administrator";
    case "LOCKED":
      return "Account has been locked due to security concerns";
    case "ARCHIVED":
      return "Account has been archived and is no longer active";
    default:
      return "Account status information";
  }
};

export const getRoleColor = (
  role: OrgRole | string,
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (role) {
    case "OWNER":
      return "error"; // Red/pink for highest privilege
    case "ADMIN":
      return "secondary"; // Purple for admin
    case "MEMBER":
      return "primary"; // Blue for regular member
    case "VIEWER":
      return "default"; // Gray for read-only
    default:
      return "default";
  }
};

export const getStatusColor = (
  status: AccountStatus | string,
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "SUSPENDED":
    case "LOCKED":
      return "error";
    case "UNVERIFIED":
      return "warning";
    case "CREATED":
      return "info";
    case "ONBOARDING":
      return "primary";
    case "ARCHIVED":
      return "default"; // Gray/muted styling for archived users
    default:
      return "default";
  }
};
